import React from 'react';
import { Star } from '../lib/icons';
import { useRatings } from '../hooks/useRatings';

interface RatingDisplayProps {
  doctorId: string;
  showReviews?: boolean;
  className?: string;
  variant?: 'default' | 'light';
}

const RatingDisplay: React.FC<RatingDisplayProps> = ({ 
  doctorId, 
  showReviews = true, 
  className = "",
  variant = 'default'
}) => {
  const { ratings, loading, error, averageRating, totalRatings } = useRatings(doctorId);

  if (loading) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="animate-pulse flex items-center">
          <div className={`h-4 w-16 rounded mr-2 ${
            variant === 'light' ? 'bg-white bg-opacity-30' : 'bg-gray-200'
          }`}></div>
          <div className={`h-4 w-12 rounded ${
            variant === 'light' ? 'bg-white bg-opacity-30' : 'bg-gray-200'
          }`}></div>
        </div>
      </div>
    );
  }

  if (error || totalRatings === 0) {
    return null;
  }

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    const starColor = variant === 'light' 
      ? 'text-yellow-300 fill-current' 
      : 'text-yellow-400 fill-current';
    const emptyStarColor = variant === 'light' 
      ? 'text-white text-opacity-40' 
      : 'text-gray-300';

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className={`w-5 h-5 ${starColor}`} />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <Star className={`w-5 h-5 ${emptyStarColor}`} />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <Star className={`w-5 h-5 ${starColor}`} />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className={`w-5 h-5 ${emptyStarColor}`} />
        );
      }
    }

    return stars;
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center mr-2">
        {renderStars(averageRating)}
      </div>
      <span className={`text-lg font-semibold mr-2 ${
        variant === 'light' ? 'text-white' : 'text-gray-900'
      }`}>
        {averageRating.toFixed(1)}
      </span>
      {showReviews && (
        <span className={`text-sm ${
          variant === 'light' ? 'text-white text-opacity-80' : 'text-gray-500'
        }`}>
          ({totalRatings} {totalRatings === 1 ? 'Bewertung' : 'Bewertungen'})
        </span>
      )}
    </div>
  );
};

export default RatingDisplay;